import i18next from 'i18next';
import { toast } from 'sonner';
import { useDpaStore } from '@/store/dpa.store.js';
import { useTenantStore } from '@/store/tenant.store.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class DpaIntegration {
    constructor(sdkService, vendorService) {
        this.service = sdkService;
        this.vendorService = vendorService;
    }

    async fetchItems(params = {}) {
        const store = useDpaStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;
        store.setLoading(true);
        if (store.clearError) store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: getIntegrationErrorMessage({ message: 'Tenant context required' }) };
        }

        try {
            const page = params.page || store.paginationData?.currentPage || 1;
            const pageSize = params.pageSize || store.paginationData?.itemsPerPage || 10;
            const search = params.search ?? '';
            const sortBy = params.sortBy || 'createdAt';
            const sortOrder = params.sortOrder || 'desc';
            const columnFilters = params.columnFilters ?? store.columnFilters ?? {};

            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                ...Object.entries(columnFilters).reduce((acc, [key, value]) => {
                    if (value?.length) acc[`filter_${key}`] = value;
                    return acc;
                }, {}),
            });

            store.setItems(response.data || []);
            if (response.pagination) {
                store.setPaginationData({
                    currentPage: response.pagination.page,
                    totalPages: response.pagination.totalPages,
                    totalItems: response.pagination.total,
                    itemsPerPage: response.pagination.pageSize,
                });
            }
            if (response.uniqueValues) store.setUniqueValues(response.uniqueValues);
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (store.setError) store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async createItem(data) {
        const store = useDpaStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.create(data);
            toast.success(
                i18next.t('gdprDpa:messages.createSuccess', { defaultValue: 'DPA record created' }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async updateItem(id, data) {
        const store = useDpaStore.getState();
        store.setLoading(true);
        try {
            const response = await this.service.update(id, data);
            toast.success(
                i18next.t('gdprDpa:messages.updateSuccess', { defaultValue: 'DPA record updated' }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async deleteItem(id) {
        const store = useDpaStore.getState();
        store.setLoading(true);
        try {
            await this.service.delete(id);
            toast.success(
                i18next.t('gdprDpa:messages.deleteSuccess', { defaultValue: 'DPA record deleted' }),
            );
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchByVendor(vendorId) {
        try {
            const response = await this.service.getByVendor(vendorId);
            return { success: true, data: response.data || [] };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async addSubProcessor(id, data) {
        try {
            const response = await this.service.addSubProcessor(id, data);
            toast.success(
                i18next.t('gdprDpa:messages.subProcessorAdded', { defaultValue: 'Sub-processor added — pending approval' }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async decideSubProcessor(id, subId, decision) {
        try {
            const response = await this.service.decideSubProcessor(id, subId, { decision });
            toast.success(
                i18next.t('gdprDpa:messages.subProcessorDecided', { defaultValue: 'Sub-processor decision saved' }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    /** Form vendor dropdown seçenekleri. */
    async fetchVendorOptions() {
        try {
            const response = await this.vendorService.getAll({ page: 1, pageSize: 500, sortBy: 'name', sortOrder: 'asc' });
            return {
                success: true,
                data: (response.data || []).map((v) => ({ value: v._id, label: v.name })),
            };
        } catch {
            return { success: false, data: [] };
        }
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }
}
